import {
  useAuth,
  useClerk,
  useOrganization,
  useUser,
} from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

import AuthLoadingScreen from "../loading/auth";

import { isMockEnabled } from "@/mock";
import { getOrganizationToActivate } from "@/utils/organization";
export const AuthRoute = () => {
  const [isLoading, setIsLoading] = useState(false);
  const hasActivated = useRef(false);

  const { pathname } = useLocation();
  const { userId, isLoaded } = useAuth();
  const { user } = useUser();
  const { setActive, session } = useClerk();
  const { organization } = useOrganization();
  const isMock = isMockEnabled();

  useEffect(() => {
    const activateOrganization = async () => {
      // Only activate once per session
      if (
        hasActivated.current ||
        !user ||
        !user.organizationMemberships.length
      ) {
        return;
      }

      const organizationId = getOrganizationToActivate(
        user.organizationMemberships,
      );

      // Don't call setActive if we're already on the correct organization
      if (organizationId && organization?.id !== organizationId) {
        try {
          await setActive({
            session,
            organization: organizationId,
          });
          hasActivated.current = true;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error while switching to organization", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
        hasActivated.current = true;
      }
    };

    if (user && user.organizationMemberships.length > 0) {
      activateOrganization();
    }
  }, [user?.id, session?.id, organization?.id, setActive]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user?.id,
        email: user.emailAddresses[0].emailAddress,
        username: user.fullName ?? undefined,
      });
    }

    if (user?.externalId) {
      Sentry.setTag("replyn_user_id", user.externalId);
    }

    if (organization) {
      Sentry.setTag("organization_id", organization.id);
      Sentry.setTag("organization_name", organization.name);
    }
  }, [
    user?.id,
    user?.externalId,
    user?.emailAddresses,
    user?.fullName,
    session?.id,
    organization?.id,
  ]);

  // In mock mode Clerk never initializes (no network/login), so don't block
  // rendering on its loaded state — the app runs entirely on seeded mock data.
  if (!isMock && (!isLoaded || isLoading)) {
    return <AuthLoadingScreen />;
  }

  if (!userId && isLoaded && !isMock) {
    return <Navigate to={"/auth/sign-up"} />;
  }

  if (
    user &&
    (!user.externalId || user?.organizationMemberships.length === 0) &&
    pathname !== "/auth/onboarding" &&
    !isMock
  ) {
    return <Navigate to={"/auth/onboarding"} />;
  }

  return <Outlet />;
};
